import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchPromptHistory,
  fetchPrompts,
  placeholdersIn,
  resetPrompt,
  savePrompt,
  wordCount,
  type AdminPrompt,
  type PromptSave,
} from '../admin.js';
import type { Me } from '../identity.js';
import { isAbortError } from '../project.js';
import { AdminHeader } from './Admin.js';

/**
 * Admin › Prompts (prd-deltas §31): every prompt Civil sends to a model, grouped by what
 * it is part of. Each shows what it is for, the text in force, and its default; an edit
 * is saved with an optional note, kept as history, and checked by the runner before it
 * is kept. What the code enforces — the checks on generated code, evidence, the shapes
 * answers must take — holds whatever a prompt says, and the page says so.
 */

type Load =
  | { status: 'loading' }
  | { status: 'ready'; prompts: AdminPrompt[]; seconds: number }
  | { status: 'error'; message: string };

const selectedFromUrl = (): string | null => new URLSearchParams(window.location.search).get('prompt');

export function AdminPrompts({ me }: { me: Me }) {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [selected, setSelected] = useState<string | null>(selectedFromUrl);
  const [effect, setEffect] = useState<{ text: string; failed: boolean } | null>(null);

  const refresh = useCallback((signal?: AbortSignal) => {
    void fetchPrompts(signal)
      .then(({ prompts, takesEffectWithinSeconds }) => setLoad({ status: 'ready', prompts, seconds: takesEffectWithinSeconds }))
      .catch((error: unknown) => {
        if (!isAbortError(error)) setLoad({ status: 'error', message: (error as Error).message });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const prompts = load.status === 'ready' ? load.prompts : [];
  const current = prompts.find((p) => p.name === selected) ?? prompts[0];

  const choose = (name: string) => {
    setSelected(name);
    setEffect(null);
    const url = new URL(window.location.href);
    url.searchParams.set('prompt', name);
    window.history.replaceState(null, '', url);
  };

  const areas = useMemo(() => {
    const out = new Map<string, AdminPrompt[]>();
    for (const p of prompts) out.set(p.area, [...(out.get(p.area) ?? []), p]);
    return [...out.entries()];
  }, [prompts]);

  return (
    <div className="admin">
      <AdminHeader me={me} current="/admin/prompts" />
      <p className="muted admin-lede">
        Every prompt Civil sends to a model. An edit applies to every project and every user
        {load.status === 'ready' ? ` within ${load.seconds} seconds` : ''}. Checks Civil makes in code
        — what generated code may be, evidence in the code, the shape of every answer — hold
        whatever a prompt says.
      </p>
      {effect ? <p className={effect.failed ? 'admin-effect failed' : 'admin-effect'}>{effect.text}</p> : null}
      {load.status === 'loading' ? <p className="muted">Loading…</p> : null}
      {load.status === 'error' ? <p className="gate-error">{load.message}</p> : null}
      {load.status === 'ready' && current ? (
        <div className="prompts">
          <nav className="prompts-list" aria-label="Prompts">
            {areas.map(([area, list]) => (
              <section key={area}>
                <h2>{area}</h2>
                <ul>
                  {list.map((p) => (
                    <li key={p.name}>
                      <button
                        type="button"
                        className={p.name === current.name ? 'prompts-item current' : 'prompts-item'}
                        aria-current={p.name === current.name ? 'true' : undefined}
                        onClick={() => choose(p.name)}
                      >
                        <span>{p.title}</span>
                        {p.edited ? <span className="prompts-edited">edited</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </nav>
          <PromptEditor
            key={`${current.name}:${current.edited?.at ?? 'default'}`}
            prompt={current}
            onDone={(text, failed) => {
              setEffect({ text, failed });
              if (!failed) refresh();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function PromptEditor({ prompt, onDone }: { prompt: AdminPrompt; onDone: (effect: string, failed: boolean) => void }) {
  const [text, setText] = useState(prompt.text);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showDefault, setShowDefault] = useState(false);
  const [history, setHistory] = useState<PromptSave[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchPromptHistory(prompt.name, controller.signal)
      .then(setHistory)
      .catch(() => setHistory([]));
    return () => controller.abort();
  }, [prompt.name, prompt.edited?.at]);

  const dirty = text !== prompt.text;
  const words = wordCount(text);
  const given = placeholdersIn(text);
  const missing = prompt.fields.filter((f) => !given.includes(f));
  const extra = prompt.fields.length ? given.filter((f) => !prompt.fields.includes(f)) : [];

  // Leaving with unsaved text loses it; the browser asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const act = (work: () => Promise<string>) => {
    setBusy(true);
    void work()
      .then((effect) => onDone(effect, false))
      .catch((error: unknown) => onDone((error as Error).message, true))
      .finally(() => setBusy(false));
  };

  return (
    <section className="prompt-editor" aria-labelledby="prompt-title">
      <header className="prompt-head">
        <h2 id="prompt-title">{prompt.title}</h2>
        <code className="prompt-name">{prompt.name}</code>
      </header>
      <p className="prompt-about">{prompt.description}</p>
      <p className="prompt-state">
        {prompt.edited
          ? `Edited${prompt.edited.by ? ` by ${prompt.edited.by}` : ''} on ${when(prompt.edited.at)}${prompt.edited.note ? ` — “${prompt.edited.note}”` : ''}.`
          : 'The default, as written in the code.'}
      </p>

      <textarea
        className="prompt-text"
        value={text}
        onChange={(event) => setText(event.target.value)}
        spellCheck={false}
        aria-label={`${prompt.title} prompt text`}
      />

      <div className="prompt-checks">
        <span className={prompt.budget && words > prompt.budget ? 'prompt-check warn' : 'prompt-check'}>
          {words} words{prompt.budget ? ` · the default is held to ${prompt.budget}` : ''}
        </span>
        {prompt.fields.length ? (
          <span className={missing.length || extra.length ? 'prompt-check bad' : 'prompt-check'}>
            {missing.length
              ? `Keep ${missing.map((f) => `{${f}}`).join(', ')}: the code fills it`
              : extra.length
                ? `Remove ${extra.map((f) => `{${f}}`).join(', ')}: the code fills only ${prompt.fields.map((f) => `{${f}}`).join(', ')}`
                : `Keeps ${prompt.fields.map((f) => `{${f}}`).join(', ')}`}
          </span>
        ) : null}
      </div>

      <div className="prompt-actions">
        <input
          type="text"
          placeholder="why (optional)"
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          aria-label="Why this edit"
        />
        <button type="button" className="connect" disabled={busy || !dirty || missing.length > 0 || extra.length > 0} onClick={() => act(() => savePrompt(prompt.name, text, note))}>
          Save
        </button>
        <button type="button" className="link" disabled={busy || !dirty} onClick={() => setText(prompt.text)}>
          Discard changes
        </button>
        {prompt.edited ? (
          <button type="button" className="link" disabled={busy} onClick={() => act(() => resetPrompt(prompt.name))}>
            Back to the default
          </button>
        ) : null}
        <button type="button" className="link" onClick={() => setShowDefault((v) => !v)} aria-expanded={showDefault}>
          {showDefault ? 'Hide the default' : 'Show the default'}
        </button>
      </div>

      {showDefault ? (
        <div className="prompt-default">
          <div className="prompt-default-head">
            <span>Default</span>
            <button type="button" className="link" onClick={() => setText(prompt.default)} disabled={text === prompt.default}>
              Use it in the editor
            </button>
          </div>
          <pre>{prompt.default}</pre>
        </div>
      ) : null}

      <section className="prompt-history">
        <h3>History</h3>
        {history === null ? <p className="muted">Loading…</p> : null}
        {history && history.length === 0 ? <p className="muted">Never edited.</p> : null}
        {history && history.length ? (
          <ol>
            {history.map((h, i) => (
              <li key={`${h.at}-${i}`}>
                <span className="admin-date">{when(h.at)}</span>
                <span className="prompt-history-what">
                  {h.text === null ? 'Back to the default' : `${wordCount(h.text)} words`}
                  {h.editedByEmail ? ` · ${h.editedByEmail}` : ''}
                  {h.note ? ` · “${h.note}”` : ''}
                </span>
                {h.text !== null && h.text !== text ? (
                  <button type="button" className="link" onClick={() => setText(h.text!)}>
                    Use in the editor
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
        ) : null}
      </section>
    </section>
  );
}

const when = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
